import { useModal } from 'contexts/ModalContext'
import { Field, Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import * as Yup from 'yup'

export default function UserFormDetail({
  isNew,
  fields,
  handleSubmit,
  handleDelete
}) {
  // Hook para gestionar el modal
  const { modal, openModal } = useModal()

  // Esquema de validación del formulario (POST o PUT):
  const validationSchema = isNew
    ? Yup.object().shape({
        username: Yup.string()
          .required('Camp obligatori')
          .min(3, 'Massa curt')
          .max(100, 'Massa llarg'),
        email: Yup.string()
          .email('Correu invàlid')
          .required('Introdueixi un correu electrònic'),
        first_name: Yup.string()
          .required('Camp obligatori')
          .min(3, 'Massa curt')
          .max(100, 'Massa llarg'),
        last_name: Yup.string().nullable().max(100, 'Massa llarg'),
        password: Yup.string()
          .required('Introdueixi una contrasenya')
          .oneOf(
            [Yup.ref('password_confirmation')],
            'Les contrasenyes han de coincidir'
          )
          .min(6, 'La contrasenya és massa curta. Mínim 6 caràcters'),
        password_confirmation: Yup.string()
          .required('Introdueixi una contrasenya')
          .oneOf([Yup.ref('password')], 'Les contrasenyes han de coincidir')
          .min(6, 'La contrasenya és massa curta. Mínim 6 caràcters')
      })
    : Yup.object().shape({
        username: Yup.string()
          .required('Camp obligatori')
          .min(3, 'Massa curt')
          .max(100, 'Massa llarg'),
        email: Yup.string()
          .email('Correu invàlid')
          .required('Introdueixi un correu electrònic'),
        first_name: Yup.string()
          .required('Camp obligatori')
          .min(3, 'Massa curt')
          .max(100, 'Massa llarg'),
        last_name: Yup.string().nullable().max(100, 'Massa llarg'),
        password: Yup.string()
          .nullable(true)
          .oneOf(
            [Yup.ref('password_confirmation')],
            'Les contrasenyes han de coincidir'
          )
          .min(6, 'La contrasenya és massa curta. Mínim 6 caràcters'),
        password_confirmation: Yup.string()
          .nullable(true)
          .oneOf([Yup.ref('password')], 'Les contrasenyes han de coincidir')
          .min(6, 'La contrasenya és massa curta. Mínim 6 caràcters')
      })

  return (
    <>
      <Formik
        initialValues={{ ...fields }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          handleSubmit(values)
        }}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          setFieldValue,
          submitForm
        }) => (
          <Form className="flex flex-col w-full p-6 mx-auto mt-4 bg-white border border-[#c8e6da] rounded-md sm:mt-6 dark:border-gray-700 dark:bg-gray-800">
            <div className="grid grid-cols-12 gap-4">
              {/* Usuari */}
              <div className="col-span-12 md:col-span-6">
                <label
                  className={`${touched.username && errors.username ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="username"
                >
                  Usuari
                </label>

                <Field
                  id="username"
                  name="username"
                  type="text"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring ${touched.username && errors.username ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-[#006F53] focus:ring-[#4a9e7e] dark:focus:border-[#4a9e7e]'}`}
                  autoComplete="new-password"
                  placeholder={'example@example.com'}
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.username && errors.username ? 'visible' : 'invisible'}`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.username || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Correu */}
              <div className="col-span-12 md:col-span-6">
                <label
                  className={`${touched.email && errors.email ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="email"
                >
                  Correu
                </label>

                <Field
                  id="email"
                  name="email"
                  type="email"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring ${touched.email && errors.email ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-[#006F53] focus:ring-[#4a9e7e] dark:focus:border-[#4a9e7e]'}`}
                  autoComplete="new-password"
                  placeholder={'example@example.com'}
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.email && errors.email ? 'visible' : 'invisible'}`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.email || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Nom */}
              <div className="col-span-12 md:col-span-6">
                <label
                  className={`${touched.first_name && errors.first_name ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="first_name"
                >
                  Nom
                </label>

                <Field
                  id="first_name"
                  name="first_name"
                  type="text"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring ${touched.first_name && errors.first_name ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-[#006F53] focus:ring-[#4a9e7e] dark:focus:border-[#4a9e7e]'}`}
                  autoComplete="new-password"
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.first_name && errors.first_name ? 'visible' : 'invisible'}`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.first_name || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* COGNOMS */}
              <div className="col-span-12 md:col-span-6">
                <label
                  className={`${touched.last_name && errors.last_name ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="last_name"
                >
                  Cognoms
                </label>

                <Field
                  id="last_name"
                  name="last_name"
                  type="text"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring ${touched.last_name && errors.last_name ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-[#006F53] focus:ring-[#4a9e7e] dark:focus:border-[#4a9e7e]'}`}
                  autoComplete="new-password"
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.last_name && errors.last_name ? 'visible' : 'invisible'}`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.last_name || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contrasenya */}
              <div className="col-span-12 md:col-span-6">
                <label
                  className={`${touched.password && errors.password ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="password"
                >
                  Contrasenya
                </label>

                <Field
                  id="password"
                  name="password"
                  type="password"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring ${touched.password && errors.password ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-[#006F53] focus:ring-[#4a9e7e] dark:focus:border-[#4a9e7e]'}`}
                  autoComplete="new-password"
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.password && errors.password ? 'visible' : 'invisible'}`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.password || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Repetir contrasenya */}
              <div className="col-span-12 md:col-span-6">
                <label
                  className={`${touched.password_confirmation && errors.password_confirmation ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="password_confirmation"
                >
                  Repetir contrasenya
                </label>

                <Field
                  id="password_confirmation"
                  name="password_confirmation"
                  type="password"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring ${touched.password_confirmation && errors.password_confirmation ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-[#006F53] focus:ring-[#4a9e7e] dark:focus:border-[#4a9e7e]'}`}
                  autoComplete="new-password"
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.password_confirmation && errors.password_confirmation ? 'visible' : 'invisible'}`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.password_confirmation || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6 gap-x-4">
              {!isNew && (
                <button
                  type="button"
                  onClick={() =>
                    openModal('DeleteModal', {
                      itemName: fields.username,
                      itemId: fields.id,
                      handleDelete
                    })
                  }
                  className="w-1/2 px-5 py-2 text-sm text-white transition-colors duration-200 bg-red-500 border border-red-500 rounded-md sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-red-500 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-40 focus:outline-none focus:ring focus:ring-red-500 focus:border-red-500 dark:focus:border-red-500"
                >
                  Eliminar
                </button>
              )}

              <button
                type="submit"
                className="w-1/2 px-5 py-2 text-sm text-white transition-colors duration-200 bg-[#006F53] border border-[#006F53] rounded-md sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-[#e8f5f0] enabled:hover:text-[#006F53] dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-40 focus:outline-none focus:ring focus:ring-[#006F53] focus:border-[#006F53] dark:focus:border-[#006F53]"
              >
                Guardar
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}

// Define los tipos de las props que recibe el componente actual
UserFormDetail.propTypes = {
  isNew: PropTypes.bool,
  fields: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired
}
